import React, { FunctionComponent } from "react";
import { TextArea } from "@plex/react-components";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import { ITemplateEditorOptions } from "../NodeTypes/Base";
import { getTemplateTokens, ITemplateToken } from "./templateTokens";
import "./TemplateEditorField.scss";

export interface ITemplateEditorInputProps extends INodeConfigPropertiesProps {
  label: string;
}

interface ITokenGroup {
  group?: string;
  tokens: ITemplateToken[];
}

const groupTokens = (tokens: ITemplateToken[]): ITokenGroup[] => {
  const groups: ITokenGroup[] = [];
  tokens.forEach((token) => {
    let group = groups.find((g) => g.group === token.group);
    if (!group) {
      group = { group: token.group, tokens: [] };
      groups.push(group);
    }
    group.tokens.push(token);
  });
  return groups;
};

export const TemplateEditorField: FunctionComponent<ITemplateEditorInputProps> = (props) => {
  const options = props.options as ITemplateEditorOptions;
  const initialValue = props.node?.data.nodeProperties[props.name] || "";

  const [state, setState] = React.useState({ value: initialValue });
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = React.useRef<number | null>(null);

  // Restore focus + caret after a programmatic insert (the controlled value change resets it).
  React.useLayoutEffect(() => {
    const el = textAreaRef.current;
    if (el && pendingCaretRef.current !== null) {
      el.focus();
      el.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
      pendingCaretRef.current = null;
    }
  }, [state.value]);

  const insertToken = (token: string) => {
    const el = textAreaRef.current;
    // Work against the DOM textarea value (always "\n"-based) so caret indices stay consistent.
    const current = el ? el.value : state.value;
    const focused = !!el && document.activeElement === el;
    const start = focused ? el!.selectionStart : current.length;
    const end = focused ? el!.selectionEnd : current.length;

    const newValue = current.slice(0, start) + token + current.slice(end);
    props.node!.data.nodeProperties[props.name] = newValue;
    pendingCaretRef.current = start + token.length;
    setState({ value: newValue });
    props.forceNodeUpdate();
  };

  const tokens = getTemplateTokens(props.node, options);
  const groups = groupTokens(tokens);

  return (
    <div className="template-editor-field">
      <TextArea
        key={`${props.sectionName} ${props.label} Input`}
        ref={textAreaRef}
        value={state.value}
        autoGrow
        onChange={(value) => {
          const newValue = value || "";
          if (newValue.trim() === "" && newValue !== "") {
            return;
          }
          props.node!.data.nodeProperties[props.name] = newValue;
          setState({ value: newValue });
          props.forceNodeUpdate();
        }}
        style={{
          minHeight: 24,
          minWidth: 200
        }}
      />
      <div className="template-editor-field__tokens" data-testid="templateTokens">
        {tokens.length === 0 ? (
          <div className="template-editor-field__hint">Select an Input Type and Schema to see available tokens.</div>
        ) : (
          <>
            <div className="template-editor-field__tokens-label">Insert token:</div>
            {groups.map((group) => (
              <div className="template-editor-field__group" key={group.group ?? "default"}>
                {group.group && <div className="template-editor-field__group-label">{group.group}</div>}
                {group.tokens.map((token) => (
                  <button
                    type="button"
                    key={token.token + token.label}
                    className="template-editor-field__token"
                    title={`Insert ${token.token}`}
                    data-testid={`templateToken_${token.token.replace(/[^a-zA-Z0-9]/g, "")}`}
                    // Keep the textarea focused so the caret position survives the click.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertToken(token.token)}
                  >
                    <span className="template-editor-field__token-name">{token.label}</span>
                    <span className="template-editor-field__token-code">{token.token}</span>
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
